import Styled, { css, keyframes } from 'styled-components';

import { media } from '../../lib/StyledComponents';
import { Button, IButtonProps } from '../Button';

export interface IModalOverlay {
  show: boolean;
  takeOver?: boolean;
  drawer?: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    top: 100px;
  }

  to {
    opacity: 1;
    top: 0;
  }
`;

const fadeOutDown = keyframes`
  from {
    opacity: 1;
    top: 0;
  }

  to {
    opacity: 0;
    top: 100px;
  }
`;

const slideInUp = keyframes`
  from {
    bottom: -100px;
  }

  to {
    bottom: 0;
  }
`;

const slideOutDown = keyframes`
  from {
    bottom: 0;
  }

  to {
    bottom: -100px;
  }
`;

export const animationDuration = 150;

export const ModalDialog = Styled.div<{ maxWidth?: string }>`
  width: 100%;
  margin-top: auto;
  margin-bottom: auto;
  position: relative;
  color: ${({ theme: { modal } }) => modal.colorText};
  background: ${({ theme }) => theme.colorBackground};

  /* override headline text color */
  h1,
  h2,
  h3 {
    color: ${({ theme: { modal } }) => modal.colorHeadlineText};
  }

  ${({ maxWidth, theme: { modal } }) => css`
    ${media.medium`
      max-width: ${maxWidth || modal.width};
      border-radius: ${modal.borderRadius};
      box-shadow: ${modal.colorBoxShadow};
    `}
  `}
`;

ModalDialog.displayName = 'ModalDialog';

export const ModalOverlay = Styled.div<IModalOverlay>`
  display: block;
  max-width: inherit;
  width: 100vw;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 9999;
  background: ${({ theme }) => theme.modal.mobileColorBackground ? theme.modal.mobileColorBackground : theme.colorBackground};
  padding: ${({ theme }) => theme.modal.mobilePadding && theme.modal.mobilePadding};

  ${props => props.show ? css`
    animation: ${props.takeOver ? fadeInUp : fadeIn} ease-in 1;
  ` : css`
    animation: ${props.takeOver ? fadeOutDown : fadeOut} ease-out 1;
  `}

  animation-fill-mode: forwards;
  animation-duration: ${animationDuration}ms;
  animation-delay: 0ms;

  ${props => (!props.takeOver && !props.drawer) && css`
    ${media.medium`
      display: flex;
      justify-content: center;
      background: ${props.theme.modal.colorBackground};
      align-items: flex-start;
      padding: 2em 0;
    `}
  `}

  ${props => props.takeOver && css`
    padding: 0;
    background: ${props.theme.colorBackground};

    ${media.small`
      padding: 6em 0 0;
    `}
  `}

  ${props => props.drawer && css`
    display: flex;
    justify-content: center;
    background: ${props.theme.modal.colorBackground};
    align-items: flex-start;
    padding: 2em 0;

    ${ModalDialog} {
      ${props.show ? css`
        animation-name: ${slideInUp};
      ` : css`
        animation-name: ${slideOutDown};
      `}
    }
  `}
`;

ModalOverlay.defaultProps = {
  show: true,
  takeOver: false
};

ModalOverlay.displayName = 'ModalOverlay';

export const ModalCtaButton = Styled(Button)<IButtonProps>`
  max-width: 24em;
  margin: 2em auto 1em;
  text-align: center;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
`;
